import React, { useState, useEffect } from "react";
import { useData } from "../contexts/DataContext";
import PractitionerCard from "../components/PractitonerCard";
import Dropdown from "../components/Dropdown";
import ButtonSlim from "../components/Buttons/ButtonSlim";
import {
  LOCATIONS,
  COUNTRIES,
  getRegionsByCountry,
  getPractitionersByCountry,
  getPractitionersByRegion,
} from "../constants/locations";
import {
  eventTypes,
  getEventTypeLabel,
  getByType,
} from "../constants/eventTypes";

const Practitioners: React.FC = () => {
  const { providerData, loading, error } = useData();
  const [filteredPractitioners, setFilteredPractitioners] = useState<any[]>([]);
  const [practitioners, setPractitioners] = useState<any[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [regions, setRegions] = useState<any>(
    Object.values(LOCATIONS).map((location) => location.region)
  );
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);

  // Initialize practitioners with approved providers only
  useEffect(() => {
    if (providerData.length > 0) {
      const approvedProviders = providerData.filter(provider => provider.approved === true);
      setPractitioners(approvedProviders);
      setFilteredPractitioners(approvedProviders);
    }
  }, [providerData]);

  // Update regions when country changes
  useEffect(() => {
    if (selectedCountry) {
      const regions = getRegionsByCountry(selectedCountry);
      setRegions(regions);
      if (selectedRegion && !regions.includes(selectedRegion)) {
        setSelectedRegion(null);
      }
    } else {
      setRegions(Object.values(LOCATIONS).map((location) => location.region));
    }
  }, [selectedCountry]);

  // Apply filters
  useEffect(() => {
    let filtered = practitioners;

    // Filter by country using helper function
    filtered = getPractitionersByCountry(selectedCountry, filtered);

    // Filter by region using helper function
    filtered = getPractitionersByRegion(selectedRegion, filtered);

    // Filter by type using helper function
    filtered = getByType(selectedType, filtered);

    setFilteredPractitioners(filtered);
  }, [selectedCountry, selectedRegion, selectedType, practitioners]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8 text-center">Our Practitioners</h1>
      
      <div className="flex flex-col gap-4 mb-8">
        <div className="flex flex-col sm:items-center md:items-center md:flex-row md:justify-end gap-4">
          {/* Controls - Type, Country, Region */}
          <div className="flex flex-col sm:items-center md:items-start sm:flex-row gap-4">
            <div className="w-[200px] pb-4 sm:p-0 mx-auto sm:mx-auto md:ml-0 md:ml-4">
              <Dropdown
                trigger={
                  <ButtonSlim className="w-full border border-black">
                    {selectedType
                      ? getEventTypeLabel(selectedType)
                      : "Specialty"}
                  </ButtonSlim>
                }
                items={eventTypes.map((eventType) => ({
                  label: eventType.label,
                  onClick: () => setSelectedType(eventType.type),
                }))}
                showAll={true}
                selectAll={() => setSelectedType(null)}
              />
            </div>
            <div className="flex flex-row justify-center gap-4">
              <Dropdown
                trigger={
                  <ButtonSlim className="w-[150px] bg-gray-700 dark:bg-gray-700 text-white hover:bg-gray-600 dark:hover:bg-gray-600">
                    {selectedCountry || "Country"}
                  </ButtonSlim>
                }
                items={COUNTRIES.map((country) => ({
                  label: country,
                  onClick: () => setSelectedCountry(country),
                }))}
                showAll={true}
                selectAll={() => setSelectedCountry(null)}
              />
              <Dropdown
                trigger={
                  <ButtonSlim className="w-[200px] bg-gray-700 dark:bg-gray-600 text-white hover:bg-gray-700 dark:hover:bg-gray-600">
                    {selectedRegion || "Region"}
                  </ButtonSlim>
                }
                items={regions.map((region: string) => ({
                  label: region,
                  onClick: () => setSelectedRegion(region),
                }))}
                showAll={true}
                selectAll={() => setSelectedRegion(null)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPractitioners.map((provider) => (
          <PractitionerCard
            key={provider.id}
            provider={provider}
          />
        ))}
      </div>
    </div>
  );
};

export default Practitioners; 